'use client';

import { useMemo, useState, useEffect, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useDisplayTimeZone } from '@/contexts/DisplayTimeZoneContext';

import { Chip, ChipRow, FilterBar, selectValue } from '@/components/ui';
import { useNowMs } from '@/hooks/useNowMs';

import { type PublicMatch } from './libs/scheduleStatus';

import { RefreshHeader } from './components/shared/RefreshHeader';
import { eventPlayerHref } from '@/app/[locale]/event/[eventId]/libs/eventPlayers';
import { MatchDetailSheet } from './components/shared/MatchDetailSheet';
import { SlotGrid } from './components/courts/SlotGrid';
import { courtsForGrid } from './libs/slotGrid';
import { courtDelayMinutes } from '@/lib/schedule/courtDelay';
import { VENUE_DOT_CLASSES, venueColorIndex, orderCourtsByVenue } from './libs/venueColumns';
import { useEventLiveScores } from '@/app/[locale]/event/[eventId]/[slug]/components/useEventLiveScores';
import { applyScheduleScore } from '@/app/[locale]/event/[eventId]/libs/liveScoreViews';
import { joinVenueCourt } from '@/app/[locale]/event/[eventId]/libs/venues';
import { todayKeyInZone } from '@/app/[locale]/event/[eventId]/libs/matchesDay';
import { DayChipLabel } from './components/DayChipLabel';

interface ScheduleData {
  asOf: string;
  event: { id: string; name: string; start_date: string; end_date: string };
  days: string[];
  selectedDay: string;
  courts: Array<{
    id: string;
    name: string;
    label?: string;
    delayMin: number;
    liveMatchId: string | null;
    /** Absent on a payload cached by the previous deploy — read as null. */
    venueId?: string | null;
  }>;
  /** Absent on a payload cached by the previous deploy (`unstable_cache`) — read as `[]`. */
  venues?: Array<{ id: string; name: string; address: string | null; isPrimary: boolean }>;
  matches: PublicMatch[];
  /** Resolved event zone (pinned, else location, else default). Absent on a payload cached by the previous deploy. */
  timezone?: string;
}

interface SchedulePageClientProps {
  data: ScheduleData;
  slug?: string;
}

export function SchedulePageClient({ data: initialData, slug }: SchedulePageClientProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('PublicSchedule');
  const tBar = useTranslations('Common.ui.filterBar');
  const locale = useLocale();
  const pinnedZone = useDisplayTimeZone();
  // The zone the server bucketed `days` in; on a cached older payload fall back
  // to the pinned zone, then the viewer's. Only read after mount (the clock is
  // null until then), so the browser zone cannot cause a mismatch.
  const dayZone =
    initialData.timezone ?? pinnedZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  // One clock for the NOW line and the court delays: null until mounted, paused while the tab is hidden.
  const nowMs = useNowMs(30_000);
  const viewingToday = nowMs !== null && todayKeyInZone(nowMs, dayZone) === initialData.selectedDay;
  const live = useEventLiveScores(initialData.event.id, viewingToday);
  const data = useMemo(
    () => ({
      ...initialData,
      matches: initialData.matches.map((match) =>
        applyScheduleScore(match, live.byScheduleId[match.id]),
      ),
      asOf: live.updatedAt ?? initialData.asOf,
    }),
    [initialData, live.byScheduleId, live.updatedAt],
  );
  const topology = useRef<number | null>(null);
  useEffect(() => {
    if (live.updatedAt === null) return;
    const previous = topology.current;
    topology.current = live.topologyRevision;
    if (
      (previous === null && live.topologyRevision > 0) ||
      (previous !== null && previous !== live.topologyRevision)
    ) {
      const params = new URLSearchParams(searchParams.toString());
      params.set('live_topology', String(live.topologyRevision));
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    }
  }, [live.topologyRevision, live.updatedAt, router, pathname, searchParams]);

  const replaceParams = (mutate: (p: URLSearchParams) => void) => {
    const params = new URLSearchParams(searchParams.toString());
    mutate(params);
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };
  const setDay = (day: string) => replaceParams((p) => p.set('day', day));

  const venues = useMemo(() => data.venues ?? [], [data.venues]);
  const multiVenue = venues.length > 1;
  const requestedVenue = searchParams.get('venue');
  const activeVenueId =
    multiVenue && requestedVenue && venues.some((v) => v.id === requestedVenue)
      ? requestedVenue
      : 'all';
  const setActiveVenue = (id: string) =>
    replaceParams((p) => {
      if (id === 'all') p.delete('venue');
      else p.set('venue', id);
    });
  // "Venue · Court" in the match sheet on a multi-venue event, so two Court 1s differ.
  const labelFor = (c: { name: string; venueId?: string | null }) => {
    const venue = multiVenue ? venues.find((v) => v.id === (c.venueId ?? null)) : undefined;
    return venue ? joinVenueCourt(venue.name, c.name) : c.name;
  };

  const otherLabel = t('otherCourt');
  const showBands = multiVenue && activeVenueId === 'all';
  // The grid's columns: the venue's courts (or all, grouped by venue), each with its honest delay.
  // Memoised so the row grouping recomputes only on data/venue/clock changes, not every render.
  const { gridCourts, gridMatches } = useMemo(() => {
    const scoped =
      activeVenueId === 'all'
        ? orderCourtsByVenue(data.courts, multiVenue ? venues : [])
        : data.courts.filter((c) => (c.venueId ?? null) === activeVenueId);
    const named = scoped.map((c) => ({
      ...c,
      delayMin: viewingToday
        ? (courtDelayMinutes(
            data.matches.filter((m) => m.courtId === c.id),
            nowMs,
          ) ?? 0)
        : 0,
    }));
    const ids = new Set(scoped.map((c) => c.id));
    const inScope =
      activeVenueId === 'all' ? data.matches : data.matches.filter((m) => ids.has(m.courtId));
    return {
      // The "other" column (matches on removed courts) exists only under All venues.
      gridCourts: courtsForGrid(named, activeVenueId === 'all' ? data.matches : [], otherLabel),
      gridMatches: inScope,
    };
  }, [data, venues, multiVenue, activeVenueId, otherLabel, viewingToday, nowMs]);

  const venueCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const courtVenues = new Map(data.courts.map((c) => [c.id, c.venueId]));
    for (const match of data.matches) {
      const venueId = courtVenues.get(match.courtId);
      if (venueId && venues.some((venue) => venue.id === venueId))
        counts[venueId] = (counts[venueId] ?? 0) + 1;
    }
    return counts;
  }, [data.courts, data.matches, venues]);
  const [openMatchId, setOpenMatchId] = useState<string | null>(null);
  const openMatch = useMemo(
    () => (openMatchId ? (data.matches.find((m) => m.id === openMatchId) ?? null) : null),
    [openMatchId, data.matches],
  );
  const openMatchCourt = openMatch
    ? (gridCourts.find((c) => c.id === openMatch.courtId) ?? null)
    : null;
  const openMatchCourtData = openMatch
    ? (data.courts.find((c) => c.id === openMatch.courtId) ?? null)
    : null;

  // No card around the grid: its own bordered scroll box is the only frame.
  const timeline = (
    <SlotGrid
      matches={gridMatches}
      axisMatches={data.matches}
      courts={gridCourts}
      venues={showBands ? venues : undefined}
      onTap={setOpenMatchId}
      day={data.selectedDay}
      zone={dayZone}
      nowMs={nowMs}
    />
  );

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex items-center justify-between gap-2 print:hidden">
        {data.days.length > 1 && (
          <ChipRow
            label={t('selectDay')}
            activeKey={data.selectedDay}
            // Fill the row and stop at the refresh button instead of bleeding under it.
            className="mr-0 min-w-0 flex-1 pr-0 md:mr-0 md:pr-0"
          >
            {data.days.map((d) => (
              <Chip key={d} selected={d === data.selectedDay} onClick={() => setDay(d)}>
                <DayChipLabel dayKey={d} locale={locale} />
              </Chip>
            ))}
          </ChipRow>
        )}
        <RefreshHeader
          asOf={data.asOf}
          onRefresh={() => {
            void live.refresh();
            router.refresh();
          }}
        />
      </div>

      {multiVenue ? (
        <FilterBar
          presentation="sidebar"
          groups={[
            {
              kind: 'select',
              id: 'venue',
              label: t('venueFilter'),
              allLabel: t('allVenues'),
              allCount: data.matches.length,
              options: venues.map((v) => ({
                value: v.id,
                label: v.name,
                count: venueCounts[v.id] ?? 0,
                adornment: (
                  <span
                    aria-hidden="true"
                    className={`size-1.5 shrink-0 rounded-full ${VENUE_DOT_CLASSES[venueColorIndex(venues, v.id)!]}`}
                  />
                ),
              })),
            },
          ]}
          value={{ venue: activeVenueId === 'all' ? undefined : activeVenueId }}
          onChange={(next) => setActiveVenue(selectValue(next, 'venue') ?? 'all')}
          resultCount={gridMatches.length}
          labels={{
            filters: tBar('filters'),
            filtersApplied: (count) => tBar('filtersApplied', { count }),
            clearAll: tBar('clearAll'),
            reset: tBar('reset'),
            show: (count) => tBar('show', { count }),
            remove: (label) => tBar('remove', { label }),
          }}
        >
          {timeline}
        </FilterBar>
      ) : (
        timeline
      )}

      <MatchDetailSheet
        match={openMatch}
        playerHref={
          slug
            ? (id, division) =>
                `/${locale}${eventPlayerHref(data.event.id, slug, id, division ?? undefined)}`
            : undefined
        }
        courtName={openMatchCourtData ? labelFor(openMatchCourtData) : ''}
        delayMin={openMatchCourt?.delayMin ?? 0}
        onClose={() => setOpenMatchId(null)}
      />
    </div>
  );
}
